/**
 * React Query Configuration
 *
 * This file contains the stale time configuration for all TanStack Query
 * queries used in the application.
 *
 * Values can be configured through environment variables.
 */

// Stale times in milliseconds
export const REACT_QUERY_STALE_TIMES = {
  // Default stale time for queries that don't specify one
  DEFAULT: parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_DEFAULT) || 0, // 0 seconds

  // Authentication and User
  USER: parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_USER) || 300000, // 5 minutes

  // Organization
  ORGANIZATION: parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_ORGANIZATION) || 600000, // 10 minutes

  // Products
  PRODUCTS: parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_PRODUCTS) || 900000, // 15 minutes
  PRODUCT_STATS: parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_PRODUCT_STATS) || 120000, // 2 minutes

  // Subscriptions
  SUBSCRIPTIONS: parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_SUBSCRIPTIONS) || 300000, // 5 minutes
  SUBSCRIPTION_TIERS:
    parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_SUBSCRIPTION_TIERS) || 1800000, // 30 minutes
  SUBSCRIPTION_CYCLES:
    parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_SUBSCRIPTION_CYCLES) || 1800000, // 30 minutes

  // Configuration
  COUNTRIES: parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_COUNTRIES) || 3600000, // 60 minutes
  INDUSTRIES: parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_INDUSTRIES) || 3600000, // 60 minutes
  FREQUENCIES: parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_FREQUENCIES) || 1800000, // 30 minutes

  // Measurements
  MEASUREMENT_SET: parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_MEASUREMENT_SET) || 0, // 0 seconds
  MEASUREMENT_SETS: parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_MEASUREMENT_SETS) || 0, // 0 seconds
  MEASUREMENTS: parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_MEASUREMENTS) || 0, // 0 seconds
  PARTICIPANT_LIST: parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_PARTICIPANT_LIST) || 0, // 0 seconds
  SEGMENTATION_ITEMS:
    parseInt(import.meta.env.VITE_REACT_QUERY_STALE_TIME_SEGMENTATION_ITEMS) || 60000, // 1 minute
}

/**
 * Helper function to get the appropriate stale time based on query type
 * @param {string} queryType - Query type (e.g., 'user', 'products', 'measurements')
 * @returns {number} Stale time in milliseconds
 */
export const getStaleTime = queryType => {
  const upperCaseType = queryType.toUpperCase()
  return REACT_QUERY_STALE_TIMES[upperCaseType] || REACT_QUERY_STALE_TIMES.DEFAULT
}

/**
 * Global QueryClient configuration
 */
export const QUERY_CLIENT_CONFIG = {
  defaultOptions: {
    queries: {
      staleTime: REACT_QUERY_STALE_TIMES.DEFAULT,
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
}

/**
 * Constants for query types
 * Useful for maintaining consistency in naming
 */
export const QUERY_TYPES = {
  USER: 'user',
  ORGANIZATION: 'organization',
  PRODUCTS: 'products',
  PRODUCT_STATS: 'product_stats',
  SUBSCRIPTIONS: 'subscriptions',
  SUBSCRIPTION_TIERS: 'subscription_tiers',
  SUBSCRIPTION_CYCLES: 'subscription_cycles',
  COUNTRIES: 'countries',
  INDUSTRIES: 'industries',
  FREQUENCIES: 'frequencies',
  MEASUREMENT_SET: 'measurement_set',
  MEASUREMENT_SETS: 'measurement_sets',
  MEASUREMENTS: 'measurements',
  PARTICIPANT_LIST: 'participant_list',
  SEGMENTATION_ITEMS: 'segmentation_items',
}

/**
 * Helper function to create query configuration with appropriate stale time
 * @param {string} queryType - Query type
 * @param {Object} additionalConfig - Additional configuration for the query
 * @returns {Object} Complete query configuration
 */
export const createQueryConfig = (queryType, additionalConfig = {}) => {
  return {
    staleTime: getStaleTime(queryType),
    ...additionalConfig,
  }
}
